import { Box } from '@mui/material'
import React, { useEffect, useMemo, useRef } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from '@/shared/ui/ErrorFallback'
import useEnterPageLogAnalytics from '@/shared/hooks/useEnterPageLogAnalytics'
import ScrollToTopButton from '@/components/tv/components/ScrollToTopButton'
import { useHomeContent } from '@/pages/home/hooks/useHomeContent'
import { useVODStore } from '@/shared/store'
import { pageType } from '@/shared/store/types'
import { useAtmospheric } from '@/features/VOD/hooks/useAtmospheric'
import { VodRowViewProps } from '@/types/vod/vodRowType'
import VodRowView from '@/features/VOD/components/VodRowView'
import {
    vodItemImageHeight,
    vodZeroRowItemImageHeight,
    ZERO_ROW,
} from '@/shared/constants'
import { VodItemsProvider } from '@/features/VOD/context/UseGetVodItemContext/VodItemsContext'
import { getGetZeroRowViewModule } from '@/shared/getContainer/getGetZeroRowViewModule'

const ZeroRowView = await getGetZeroRowViewModule()

const HomeContent = () => {
    const homeContainerRef = useRef<HTMLDivElement | null>(null)
    useEnterPageLogAnalytics('HOME')

    const containerStyle = {
        height: '90vh',
        overflowY: 'auto',
        display: 'flex',
        gap: '50px',
        flexDirection: 'column',

        '& > :first-of-type': {
            // marginBottom: '-50px', // hack to reduce flex gap on first element
        },
    }

    const scrollToTop = () => {
        const container = homeContainerRef.current
        if (container)
            container?.scrollTo({
                top: 0,
                behavior: 'smooth',
            })
    }
    const vodRowsRef = useRef<HTMLDivElement[]>([])
    const { homeRows } = useHomeContent()
    const { setVodPages } = useVODStore()

    const handleGoToCategoryButtonClick = (
        itemId: string,
        title: string,
        type: pageType
    ): void => {
        setVodPages(itemId, title, type)
        homeContainerRef.current?.scrollTo(0, 0)
    }

    const { rowElements, handleScroll, handleRefsUpdate } = useAtmospheric(
        homeContainerRef,
        homeRows || []
    )
    useEffect(() => {
        handleRefsUpdate(vodRowsRef.current)
    }, [homeRows?.length, handleRefsUpdate, vodRowsRef.current])

    useEffect(() => {
        const container = homeContainerRef.current
        if (container) {
            container.addEventListener('scroll', handleScroll)
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll)
            }
        }
    }, [rowElements, homeContainerRef.current])

    const ZeroRow = useMemo(() => {
        const zeroRow = homeRows.find(
            (row: VodRowViewProps) => row.title.toLowerCase() === ZERO_ROW
        )
        if (!zeroRow) return <></>

        const {
            productId,
            query,
            serviceName,
            hideCaptions,
            hideProgress,
            rowHeightPercentage,
        } = zeroRow

        return (
            <Box
                key={productId}
                data-index={productId}
                ref={(listElement: HTMLDivElement) => {
                    vodRowsRef.current[1] = listElement
                }}>
                <VodItemsProvider
                    productId={String(productId)}
                    type={query || ''}
                    isCardOrientationPriorityHorizontal={false}
                    serviceName={serviceName || ''}>
                    <ZeroRowView
                        productId={productId}
                        hideCaptions={hideCaptions}
                        hideProgress={hideProgress}
                        rowHeightPercentage={rowHeightPercentage}
                        maxHeight={vodZeroRowItemImageHeight}
                        handleGoToCategoryButtonClick={
                            handleGoToCategoryButtonClick
                        }
                        isHomeContentRow={true}
                    />
                </VodItemsProvider>
            </Box>
        )
    }, [homeRows, handleGoToCategoryButtonClick])

    const contentToDisplay = useMemo(() => {
        if (!homeRows) return null

        return homeRows.map((row: VodRowViewProps, index: number) => {
            const {
                productId,
                title,
                hideCaptions,
                hideProgress,
                rowHeightPercentage,
                isCardOrientationPriorityHorizontal,
                hideTitle,
                query,
            } = row

            if (title.toLowerCase() !== ZERO_ROW && query)
                return (
                    <Box
                        key={productId}
                        data-index={row.productId}
                        ref={(listElement: HTMLDivElement) => {
                            vodRowsRef.current[index] = listElement
                        }}>
                        <VodItemsProvider
                            url={row.query}
                            productId={String(row.productId)}
                            type={row.query || ''}
                            serviceName={row.serviceName || ''}
                            isCardOrientationPriorityHorizontal={
                                !!isCardOrientationPriorityHorizontal
                            }>
                            <VodRowView
                                productId={productId}
                                title={title}
                                hideTitle={hideTitle}
                                hideCaptions={hideCaptions}
                                hideProgress={hideProgress}
                                rowHeightPercentage={rowHeightPercentage}
                                maxHeight={vodItemImageHeight}
                                handleGoToCategoryButtonClick={
                                    handleGoToCategoryButtonClick
                                }
                                isHomeContentRow={true}
                            />
                        </VodItemsProvider>
                    </Box>
                )
        })
    }, [homeRows, handleGoToCategoryButtonClick])

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Box sx={containerStyle} ref={homeContainerRef}>
                {ZeroRow}

                {/*<Container maxWidth="xl" disableGutters>*/}
                <Box sx={{ padding: '0 30px 0 30px' }}>{contentToDisplay}</Box>
                {/*</Container>*/}

                <ScrollToTopButton
                    handleOnClick={scrollToTop}
                    container={homeContainerRef.current}
                />
            </Box>
        </ErrorBoundary>
    )
}

export default HomeContent
